export type ParsedMessageRow = {
  title: string;
  body: string;
  category: string | null;
  brand: string | null;
  status: 'draft' | 'approved' | 'published';
};

export type ParsedMessageRowWithValidation = ParsedMessageRow & {
  rowIndex: number;
  errors: string[];
  isValid: boolean;
};

const MAX_ROWS = 500;
const VALID_STATUSES = ['draft', 'approved', 'published'] as const;

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        current += char;
        i++;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
        i++;
      } else if (char === ',') {
        fields.push(current.trim());
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }
  }

  fields.push(current.trim());
  return fields;
}

export function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  let cleaned = text;
  if (cleaned.charCodeAt(0) === 0xfeff) {
    cleaned = cleaned.slice(1);
  }

  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i];

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
    } else if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && i + 1 < cleaned.length && cleaned[i + 1] === '\n') {
        i++;
      }
      if (current.trim().length > 0) {
        lines.push(current);
      }
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim().length > 0) {
    lines.push(current);
  }

  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map(parseCsvLine);

  return { headers, rows };
}

export function validateMessageRows(
  headers: string[],
  rows: string[][],
  validCategories: readonly string[],
  validBrands: string[]
): { validRows: ParsedMessageRow[]; allRows: ParsedMessageRowWithValidation[] } {
  const titleIdx = headers.indexOf('title');
  const bodyIdx = headers.indexOf('body');
  const categoryIdx = headers.indexOf('category');
  const brandIdx = headers.indexOf('brand');
  const statusIdx = headers.indexOf('status');

  const missingHeaders: string[] = [];
  if (titleIdx === -1) missingHeaders.push('title');
  if (bodyIdx === -1) missingHeaders.push('body');

  if (missingHeaders.length > 0) {
    throw new Error(`Missing required CSV columns: ${missingHeaders.join(', ')}`);
  }

  if (rows.length > MAX_ROWS) {
    throw new Error(`CSV contains ${rows.length} rows. Maximum allowed is ${MAX_ROWS}.`);
  }

  const categoryLookup = new Map(validCategories.map((c) => [c.toLowerCase(), c]));
  const brandLookup = new Map(validBrands.map((b) => [b.toLowerCase(), b]));

  const validRowsList: ParsedMessageRow[] = [];
  const allRowsList: ParsedMessageRowWithValidation[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const errors: string[] = [];

    const rawTitle = titleIdx < row.length ? row[titleIdx] : '';
    const rawBody = bodyIdx < row.length ? row[bodyIdx] : '';
    const rawCategory = categoryIdx >= 0 && categoryIdx < row.length ? row[categoryIdx] : '';
    const rawBrand = brandIdx >= 0 && brandIdx < row.length ? row[brandIdx] : '';
    const rawStatus = statusIdx >= 0 && statusIdx < row.length ? row[statusIdx] : '';

    const title = rawTitle.trim();
    const body = rawBody.trim();

    if (!title) errors.push('Title is required');
    if (!body) errors.push('Body is required');
    if (body.length > 500) errors.push(`Body exceeds 500 characters (${body.length})`);

    let category: string | null = null;
    if (rawCategory.trim()) {
      const matched = categoryLookup.get(rawCategory.trim().toLowerCase());
      if (matched) {
        category = matched;
      } else {
        errors.push(`Invalid category: "${rawCategory.trim()}"`);
      }
    }

    let brand: string | null = 'Elderberry';
    if (rawBrand.trim()) {
      const matched = brandLookup.get(rawBrand.trim().toLowerCase());
      if (matched) {
        brand = matched;
      } else {
        errors.push(`Invalid brand: "${rawBrand.trim()}"`);
      }
    }

    let status: 'draft' | 'approved' | 'published' = 'draft';
    if (rawStatus.trim()) {
      const lower = rawStatus.trim().toLowerCase();
      if ((VALID_STATUSES as readonly string[]).includes(lower)) {
        status = lower as 'draft' | 'approved' | 'published';
      } else {
        errors.push(`Invalid status: "${rawStatus.trim()}" (must be draft, approved, or published)`);
      }
    }

    const parsed: ParsedMessageRow = { title, body, category, brand, status };
    const withValidation: ParsedMessageRowWithValidation = {
      ...parsed,
      rowIndex: i + 1,
      errors,
      isValid: errors.length === 0,
    };

    allRowsList.push(withValidation);
    if (withValidation.isValid) {
      validRowsList.push(parsed);
    }
  }

  return { validRows: validRowsList, allRows: allRowsList };
}
